import React from 'react';

import { Link } from 'react-router-dom';

import { hockeyFormatter } from '@nhl/formatter-utilities';
import PlayerLink from '@nhl/nhl-react-ui/lib/PlayerLink';

import AWARDS from '../../../redux/ducks/awards/constants';
import { FranchisePages } from '../../../redux/ducks/franchises/viewTypes';
import { ColumnDefinition } from '../../../types/ReactTableTypes';
import awardConstants, { awardStatuses } from '../../../utils/awardConstants';
import GlobalColumnFunctions from '../../../utils/globalColumnFunctions';
import { TeamDisplayModule } from '../../components';

const RUNNER_UP_HEADER_ONE = {
    'art-ross-trophy': 'Second-Place',
    'maurice-rocket-richard-trophy': 'Second-Place',
};

const RUNNER_UP_HEADER_TWO = {
    'art-ross-trophy': 'Third-Place',
    'maurice-rocket-richard-trophy': 'Third-Place',
};

const sortEmptyValuesLast = (value1, value2) => {
    if (!value1) {
        return 1;
    } else if (!value2) {
        return -1;
    }
    return typeof value1 === 'number' ? value2 - value1 : value1.localeCompare(value2);
};

const sortValues = (a, b, key) => {
    const value1 = a.values[key];
    const value2 = b.values[key];
    return sortEmptyValuesLast(value1, value2);
};

const include1RunnerUp = (slug) => {
    switch (slug) {
        case AWARDS.CLARENCE_S_CAMPBELL_BOWL:
        case AWARDS.PRESIDENTS_TROPHY:
        case AWARDS.PRINCE_OF_WALES_TROPHY:
        case AWARDS.STANLEY_CUP:
        case AWARDS.WILLIAM_M_JENNINGS_TROPHY:
            return true;
        default:
            return false;
    }
};

const include2RunnersUp = (slug) => {
    switch (slug) {
        case AWARDS.ART_ROSS_TROPHY:
        case AWARDS.CALDER_MEMORIAL_TROPHY:
        case AWARDS.FRANK_J_SELKE_TROPHY:
        case AWARDS.HART_MEMORIAL_TROPHY:
        case AWARDS.JACK_ADAMS_AWARD:
        case AWARDS.JAMES_NORRIS_MEMORIAL_TROPHY:
        case AWARDS.JIM_GREGORY_GENERAL_MANAGER_OF_THE_YEAR_AWARD:
        case AWARDS.MAURICE_ROCKET_RICHARD_TROPHY:
        case AWARDS.VEZINA_TROPHY:
        case AWARDS.LADY_BYNG_MEMORIAL_TROPHY:
            return true;
        default:
            return false;
    }
};

const includePosition = (slug) => {
    // Some trophies are more about merit than skills.
    // Some trophies (Vezina) are only available to one position
    switch (slug) {
        case AWARDS.BILL_MASTERTON_MEMORIAL_TROPHY:
        case AWARDS.JAMES_NORRIS_MEMORIAL_TROPHY:
        case AWARDS.KING_CLANCY_MEMORIAL_TROPHY:
        case AWARDS.NHL_FOUNDATION_PLAYER_AWARD:
        case AWARDS.VEZINA_TROPHY:
        case AWARDS.WILLIAM_M_JENNINGS_TROPHY:
            return false;
        default:
            return true;
    }
};

const alignLeftClass = (slug, type) => {
    return include1RunnerUp(slug) && type === awardConstants.TEAM ? 'left' : '';
};

const displayTeam = (team, season, franchises) => {
    const franchise = team && franchises.filter((franchise) => franchise.id === team.franchiseId);

    return team !== null && team !== undefined ? (
        <TeamDisplayModule
            team={team}
            season={season}
            franchisePage={
                team.league.abbreviation === 'NHL' && franchise.length > 0 && FranchisePages.SEASON_BY_SEASON_RECORD
            }
            useInTable
            append={team.league.abbreviation !== 'NHL' ? ` (${team.league.abbreviation})` : null}
        />
    ) : null;
};

const teamColumn = ({ type, slug, key, show = type !== awardConstants.TEAM }, franchises) => {
    // hide when type is 'Team' since the Winner/Runner-up columns will display a team
    return {
        Header: 'Team',
        id: `Team-${key}`,
        headerClassName: alignLeftClass(slug, type),
        className: alignLeftClass(slug, type),
        sortType: (a, b) => sortValues(a, b, `Team-${key}`),
        accessor: (original) => (original[key] ? original[key][0].team?.fullName : ''),
        Cell: ({ original }) => {
            if (!original?.[key]) {
                return null;
            }

            if (original[key].length > 1) {
                const teamIds = original[key].reduce((acc, t) => {
                    acc[t.team.id] = true;
                    return acc;
                }, {});

                // Display multiple teams when there's more than 1
                if (Object.keys(teamIds).length > 1) {
                    const teamRows = original[key].map((value, index) => {
                        return (
                            <div key={index} className="mulitLineColumn">
                                {displayTeam(value?.team, value.seasonId, franchises)}
                            </div>
                        );
                    });
                    return <div className="multiLineContainer teamContainer">{teamRows}</div>;
                }
            }

            const recipient = original[key][0];
            return displayTeam(recipient?.team, recipient.seasonId, franchises);
        },
        minWidth: 200,
        maxWidth: 250,
        show: type !== awardConstants.TEAM && type !== awardConstants.OTHER && show,
        getProps: GlobalColumnFunctions.getProps,
    };
};

type WinnerColumns = {
    minWidth?: number | string;
    maxWidth?: number | string;
    show?: boolean;
};

const getWinnerValue = (original, key, type) => {
    if (!original[key]) {
        return '';
    }
    const recipient = original[key][0];
    switch (type) {
        case awardConstants.TEAM:
            return recipient.team?.fullName;
        case awardConstants.HEAD_COACH:
            return `${recipient.coach?.lastName} ${recipient.coach?.firstName}`;
        case awardConstants.GENERAL_MANAGER:
            if (recipient.fullName) {
                const names = recipient.fullName.split(' ');
                return names.length > 1 ? `${names[1]} ${names[0]}` : recipient.fullName;
            }
        default:
            return recipient.fullName || `${recipient.player?.lastName} ${recipient.player?.firstName}`;
    }
};

const winnerColumn = (
    slug,
    type,
    franchises,
    header,
    key,
    { minWidth, maxWidth, show }: WinnerColumns
): ColumnDefinition => ({
    Header: header,
    id: `Winner-${key}`,
    accessor: (original) => getWinnerValue(original, key, type),
    headerClassName: alignLeftClass(slug, type),
    className: alignLeftClass(slug, type),
    Cell: ({ original }) => {
        if (original[key] === undefined) {
            return '';
        }
        const recipient = original[key][0];
        switch (type) {
            case awardConstants.TEAM:
                return recipient.team ? displayTeam(recipient.team, recipient.seasonId, franchises) : '';
            case awardConstants.PLAYER:
                if (original[key].length > 1) {
                    const playerRows = original[key].map((value, index) => {
                        return (
                            <div key={index} className="mulitLineColumn">
                                {playerLink(value, 'player', slug)}
                            </div>
                        );
                    });
                    return <div className="multiLineContainer">{playerRows}</div>;
                }
                return playerLink(recipient, 'player', slug);
            case awardConstants.HEAD_COACH:
                return coachLink(recipient.coach);
            default:
                if (recipient.fullName) {
                    return recipient.fullName;
                }
                return recipient.player ? `${recipient.player.firstName} ${recipient.player.lastName}` : '';
        }
    },
    minWidth: minWidth || 180,
    maxWidth: maxWidth !== undefined ? maxWidth : type === awardConstants.TEAM ? 270 : 'inherit',
    show: show !== undefined ? show : true,
    sortType: (a, b) => {
        const value1 = getWinnerValue(a.original, key, type);
        const value2 = getWinnerValue(b.original, key, type);
        return sortEmptyValuesLast(value1, value2);
    },
    getProps: GlobalColumnFunctions.getProps,
});

const positionColumn = (slug, type, key, showColumn) => ({
    id: `Position-${key}`,
    Header: 'Pos',
    accessor: (original) => (original[key] ? original[key][0].player?.position : ''),
    Cell: ({ original }) => {
        const records = original?.[key];
        if (!records) {
            return null;
        }

        const positions = records.map((record, index) => (
            <div key={index} className="mulitLineColumn">
                {record.player?.position}
            </div>
        ));
        return <div className="multiLineContainer">{positions}</div>;
    },
    show: type === 'Player' && includePosition(slug) && showColumn,
    sortType: (a, b) => sortValues(a, b, `Position-${key}`),
    minWidth: 40,
    maxWidth: 40,
    getProps: GlobalColumnFunctions.getProps,
});

const goalsColumn = (slug, key): ColumnDefinition => ({
    id: `Goals-${key}`,
    Header: 'Goals',
    accessor: (row) => row[key]?.[0]?.value || '',
    show: slug === AWARDS.MAURICE_ROCKET_RICHARD_TROPHY,
    minWidth: 50,
    getProps: GlobalColumnFunctions.getProps,
    sortType: (a, b) => sortValues(a, b, `Goals-${key}`),
});

const captainsColumn = (type, key) => ({
    Header: 'Captain(s)',
    id: `captain-${key}`,
    accessor: (val) => {
        const captains = val[key]?.[0]?.captains || [];
        return captains.length === 0 ? '--' : captains[0].player.lastName + ' ' + captains[0].player.firstName;
    },
    Cell: ({ original }) => {
        const captains = original[key]?.[0]?.captains;
        return !captains || captains.length === 0
            ? 'No Captain'
            : captains.map((captain) => <PlayerLink key={captain.playerId} player={captain.player} />);
    },
    style: { flexDirection: 'column' },
    minWidth: 130,
    show: type === AWARDS.STANLEY_CUP,
    getProps: GlobalColumnFunctions.getProps,
    sortType: (a, b) => sortValues(a, b, `captain-${key}`),
});

const coachColumn = (type, key) => ({
    Header: 'Coach',
    id: `coach-${key}`,
    accessor: (val) => (val[key] ? `${val[key][0].coach?.lastName} ${val[key][0].coach?.firstName}` : ''),
    Cell: ({ original }) => coachLink(original[key]?.[0]?.coach),
    minWidth: 130,
    show: type === awardConstants.TEAM,
    getProps: GlobalColumnFunctions.getProps,
    sortType: (a, b) => sortValues(a, b, `coach-${key}`),
});

const playerLink = (original, type, slug) => {
    return original[type] !== null && original[type] !== undefined ? (
        <>
            <PlayerLink
                player={original[type]}
                linkedContent={`${original[type].firstName} ${original[type].lastName}`}
            />
            {original.isRookie && slug !== AWARDS.CALDER_MEMORIAL_TROPHY && (
                <span className="rookie-icon" title="Rookie">
                    R
                </span>
            )}
        </>
    ) : (
        ''
    );
};

const coachLink = (coach) => {
    if (coach) {
        return (
            <a href={`/coaches/${coach.id}`} rel="noopener" target="_blank">
                {coach.firstName} {coach.lastName}
            </a>
        );
    }
    return '';
};

const columnDefinitions = (type, slug, franchises): ColumnDefinition[] => [
    {
        Header:
            slug === AWARDS.NHL_LIFETIME_ACHIEVEMENT_AWARD || slug === AWARDS.LESTER_PATRICK_TROPHY ? 'Year' : 'Season',
        id: 'season',
        className: 'column-season',
        accessor: (recipient) => hockeyFormatter.singleSeasonFormatter(recipient.seasonId),
        Cell: ({ original, value }) => {
            switch (slug) {
                case AWARDS.NHL_LIFETIME_ACHIEVEMENT_AWARD:
                    return hockeyFormatter.seasonEndYearFormatter(original.seasonId);
                case AWARDS.LESTER_PATRICK_TROPHY:
                    return hockeyFormatter.seasonEndYearFormatter(original.seasonId);
                case AWARDS.STANLEY_CUP:
                    return <Link to={`/playoff-summary/stanley-cup-winner?season=${original.seasonId}`}>{value}</Link>;
                default:
                    return value;
            }
        },
        sortDescFirst: true,
        getProps: GlobalColumnFunctions.getProps,
    },

    winnerColumn(slug, type, franchises, 'Winner', awardStatuses.WINNER, {
        minWidth: slug === AWARDS.STANLEY_CUP ? 210 : 0,
    }),
    positionColumn(slug, type, awardStatuses.WINNER, true),
    teamColumn(
        {
            key: awardStatuses.WINNER,
            type,
            slug,
        },
        franchises
    ),
    goalsColumn(slug, awardStatuses.WINNER),
    captainsColumn(slug, awardStatuses.WINNER),
    coachColumn(type, awardStatuses.WINNER),

    winnerColumn(slug, type, franchises, RUNNER_UP_HEADER_ONE[slug] || 'Runner-Up', awardStatuses.RUNNER_UP, {
        minWidth:
            slug === AWARDS.STANLEY_CUP
                ? 210
                : type === awardConstants.PLAYER || type === awardConstants.TEAM
                ? 180
                : 160,
        maxWidth: type === awardConstants.TEAM ? 270 : 'inherit',
        show: include1RunnerUp(slug) || include2RunnersUp(slug),
    }),
    positionColumn(slug, type, awardStatuses.RUNNER_UP, include1RunnerUp(slug) || include2RunnersUp(slug)),
    teamColumn(
        {
            key: awardStatuses.RUNNER_UP,
            type,
            slug,
            show: include1RunnerUp(slug) || include2RunnersUp(slug),
        },
        franchises
    ),
    goalsColumn(slug, awardStatuses.RUNNER_UP),
    captainsColumn(slug, awardStatuses.RUNNER_UP),
    coachColumn(type, awardStatuses.RUNNER_UP),

    winnerColumn(slug, type, franchises, RUNNER_UP_HEADER_TWO[slug] || 'Finalist', awardStatuses.FINALIST, {
        maxWidth: 'inherit',
        show: include2RunnersUp(slug),
    }),
    positionColumn(slug, type, awardStatuses.FINALIST, include2RunnersUp(slug)),
    teamColumn(
        {
            key: awardStatuses.FINALIST,
            type,
            slug,
            show: include2RunnersUp(slug),
        },
        franchises
    ),
    goalsColumn(slug, awardStatuses.FINALIST),
];

export default columnDefinitions;
